import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { formatDate } from '@/lib/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WhatsNewModal — fires on login for users whose `last_seen_feature_version`
 * is behind the latest version in /api/whatsnew/latest. When the user
 * dismisses (Skip / Try it now / X), we POST /api/whatsnew/dismiss/:version
 * so it never shows again.
 *
 * v2.5.5.13 — when multiple updates are unseen, the modal paginates locally
 * with prev/next buttons + dot pager. Dismissing the last (newest) one stamps
 * the user's record; dismissing any earlier one just navigates forward
 * without an API call. Closing mid-deck stamps the NEWEST shown entry so
 * older entries don't keep re-appearing on every login.
 *
 * Self-fetches the entry on mount. Pass userId only.
 */
export default function WhatsNewModal({ userId, onClose }) {
  const [entries, setEntries] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  // v2.5.7: when the server flags the user as "history mode", dismissal is
  // session-only (no last_seen stamp). The deck reappears every login so the
  // user can swipe through the full update history any time.
  const [historyMode, setHistoryMode] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/whatsnew/latest`, {
        headers: { 'X-User-Id': userId },
        params: { include_all: true },
      })
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.entries || (res?.data?.entry ? [res.data.entry] : []);
        setEntries(list);
        setHistoryMode(!!res?.data?.show_history_mode);
        setVisible(list.length > 0);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [userId]);

  const entry = entries[idx];
  const hasPrev = idx > 0;
  const hasNext = idx < entries.length - 1;

  const stampVersion = async (version) => {
    if (!version || !userId) return;
    try {
      await axios.post(
        `${API}/whatsnew/dismiss/${encodeURIComponent(version)}`,
        {},
        { headers: { 'X-User-Id': userId } }
      );
    } catch (e) { /* non-blocking */ }
  };

  const dismiss = async () => {
    // v2.5.7: history mode closes the modal for this session only — the
    // server returns the full history every login so the user can keep
    // swiping through past updates.
    if (!historyMode) {
      // Stamp the NEWEST entry in the deck so any unseen ones older than it
      // are implicitly acknowledged too — otherwise they'd re-fire on next
      // login and feel like spam.
      const newest = entries[0]?.version;
      await stampVersion(newest);
    }
    setVisible(false);
    onClose?.();
  };

  const next = () => {
    if (hasNext) setIdx(idx + 1);
    else dismiss();
  };
  const prev = () => { if (hasPrev) setIdx(idx - 1); };

  const tryNow = async () => {
    await dismiss();
    if (entry?.cta_url) {
      window.location.href = entry.cta_url;
    }
  };

  if (loading || !visible || !entry) return null;

  return (
    <div
      data-testid="whatsnew-modal-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,30,61,0.6)',
        backdropFilter: 'blur(4px)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div
        data-testid="whatsnew-modal"
        style={{
          background: '#fff', width: '100%', maxWidth: 600,
          borderRadius: 18, maxHeight: '90vh', overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          animation: 'whatsnewSlideUp 0.22s ease',
        }}
      >
        <style>{`@keyframes whatsnewSlideUp{from{transform:translateY(40px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg,#7c3aed 0%,#2563eb 100%)',
          color: '#fff', padding: '24px 24px 16px', borderRadius: '18px 18px 0 0',
          position: 'relative',
        }}>
          <button
            data-testid="whatsnew-close-btn"
            onClick={dismiss}
            style={{
              position: 'absolute', top: 18, right: 18,
              background: 'rgba(255,255,255,0.2)', border: 0, color: '#fff',
              width: 32, height: 32, borderRadius: '50%', cursor: 'pointer',
              fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label="Close"
          ><X size={16} /></button>
          <div style={{
            display: 'inline-block', padding: '4px 12px',
            background: 'rgba(255,255,255,0.2)', borderRadius: 9999,
            fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', marginBottom: 12,
          }}>New in DrawReady{entries.length > 1 ? ` · ${idx + 1} of ${entries.length}` : ''}</div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25 }}>{entry.title}</h2>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginTop: 8 }}>
            {entry.version} · Released {formatDate(entry.released)}
          </div>
        </div>

        {/* What changed */}
        <div style={{ padding: '22px 24px' }}>
          <h4 style={{
            margin: '0 0 14px', fontSize: 12, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: '#64748b', fontWeight: 700,
          }}>What changed</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {(entry.items || []).map((item, idx) => {
              const colors = [
                { bg: '#dcfce7', fg: '#166534' },
                { bg: '#dbeafe', fg: '#1e40af' },
                { bg: '#ede9fe', fg: '#5b21b6' },
                { bg: '#fef3c7', fg: '#92400e' },
              ];
              const c = colors[idx % colors.length];
              return (
                <div key={item.n || idx} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, background: c.bg, color: c.fg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, fontWeight: 700, fontSize: 14,
                  }}>{item.n || idx + 1}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{item.title}</div>
                    <div style={{ color: '#64748b', fontSize: 13, lineHeight: 1.5, marginTop: 2 }}>{item.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Why we built it */}
        {entry.why && (
          <div style={{ padding: '22px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <h4 style={{
              margin: '0 0 10px', fontSize: 12, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: '#64748b', fontWeight: 700,
            }}>Why we built it</h4>
            <p style={{ margin: 0, color: '#0f172a', fontSize: 14, lineHeight: 1.6 }}>{entry.why}</p>
          </div>
        )}

        {/* Footer actions */}
        <div style={{
          display: 'flex', gap: 10, padding: '14px 24px',
          borderTop: '1px solid #e2e8f0', background: '#f8fafc',
          borderRadius: '0 0 18px 18px', alignItems: 'center',
        }}>
          <button
            data-testid="whatsnew-skip-btn"
            onClick={dismiss}
            style={{
              padding: '10px 16px', borderRadius: 10, background: '#f1f5f9',
              border: 0, fontWeight: 600, cursor: 'pointer', color: '#0f172a',
            }}
          >{entries.length > 1 && hasNext ? 'Skip all' : 'Skip'}</button>

          {/* Dot pager — only shows when there's more than one entry */}
          {entries.length > 1 && (
            <div data-testid="whatsnew-pager" style={{ display: 'flex', gap: 6, marginLeft: 8 }}>
              {entries.map((e, i) => (
                <button
                  key={e.version}
                  data-testid={`whatsnew-dot-${i}`}
                  onClick={() => setIdx(i)}
                  aria-label={`Go to update ${i + 1} of ${entries.length}`}
                  style={{
                    width: 8, height: 8, borderRadius: '50%', border: 0,
                    background: i === idx ? '#0f172a' : '#cbd5e1',
                    cursor: 'pointer', padding: 0,
                  }}
                />
              ))}
            </div>
          )}

          <div style={{ flex: 1 }} />

          {/* Prev / Next when deck has more than one */}
          {entries.length > 1 && (
            <>
              <button
                data-testid="whatsnew-prev-btn"
                onClick={prev}
                disabled={!hasPrev}
                style={{
                  padding: '8px 10px', borderRadius: 10,
                  background: hasPrev ? '#fff' : '#f1f5f9',
                  border: '1px solid #e2e8f0', cursor: hasPrev ? 'pointer' : 'not-allowed',
                  color: hasPrev ? '#0f172a' : '#94a3b8',
                  display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600,
                }}
                aria-label="Previous update"
              ><ChevronLeft size={16} /> Back</button>
              {hasNext && (
                <button
                  data-testid="whatsnew-next-btn"
                  onClick={next}
                  style={{
                    padding: '8px 14px', borderRadius: 10, background: '#0f172a',
                    border: 0, cursor: 'pointer', color: '#fff', fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                  aria-label="Next update"
                >Next <ChevronRight size={16} /></button>
              )}
            </>
          )}

          {entry.cta_url && (!hasNext || entries.length === 1) && (
            <button
              data-testid="whatsnew-cta-btn"
              onClick={tryNow}
              style={{
                padding: '10px 16px', borderRadius: 10, background: '#16a34a',
                border: 0, color: '#fff', fontWeight: 600, cursor: 'pointer',
              }}
            >{entry.cta_text || 'Try it now'} →</button>
          )}
        </div>
      </div>
    </div>
  );
}
